import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, AlertCircle } from "lucide-react";

/**
 * Real-time product configurator.
 * Props:
 *   slug: tenant slug
 *   axes: variant_axes (definition with options + images + price_delta)
 *   variants: variants_all (each with combination, prezzo_finale, stato, stock)
 *   currentSku: current variant SKU (matches one of variants)
 *   onChange(sku): when user picks a different combination
 *   primaryColor: brand color for highlight
 *   onImageOverride(imageUrls): notify parent if variant has specific images
 */
export default function ProductConfigurator({ slug, axes, variants, currentSku, onChange, primaryColor = "#0F1B14", onImageOverride }) {
  const navigate = useNavigate();
  // Build a map: combination key -> variant
  const variantMap = useMemo(() => {
    const m = {};
    (variants || []).forEach((v) => {
      const k = Object.entries(v.combination || {}).sort().map(([k2, val]) => `${k2}:${val}`).join("|");
      m[k] = v;
    });
    return m;
  }, [variants]);

  const initialSelection = useMemo(() => {
    const cur = (variants || []).find((v) => v.sku_variante === currentSku);
    if (cur) return { ...cur.combination };
    // First available
    if (variants && variants[0]) return { ...variants[0].combination };
    return {};
  }, [currentSku, variants]);

  const [selection, setSelection] = useState(initialSelection);

  // Available option values per axis given the current selection
  const isOptionAvailable = (axisKey, value) => {
    // Check if there exists at least one variant matching this option AND constraints from OTHER axes
    return (variants || []).some((v) => {
      if (v.stato_disponibilita === "non_disponibile") return false;
      if ((v.combination || {})[axisKey] !== value) return false;
      for (const [k, val] of Object.entries(selection)) {
        if (k === axisKey) continue;
        if ((v.combination || {})[k] !== val) return false;
      }
      return true;
    });
  };

  const selectedKey = Object.entries(selection).sort().map(([k, v]) => `${k}:${v}`).join("|");
  const matchedVariant = variantMap[selectedKey];

  useEffect(() => {
    if (matchedVariant && matchedVariant.sku_variante !== currentSku) {
      // notify parent to update page (without full reload)
      onChange?.(matchedVariant.sku_variante);
    }
    if (matchedVariant?.immagini_specifiche?.length) {
      onImageOverride?.(matchedVariant.immagini_specifiche);
    }
  }, [matchedVariant?.sku_variante]); // eslint-disable-line

  const pickOption = (axisKey, value) => {
    setSelection((s) => ({ ...s, [axisKey]: value }));
  };

  if (!axes?.length) return null;

  return (
    <div className="mb-6" data-testid="product-configurator">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4" style={{ color: primaryColor }} />
        <h3 className="font-heading font-bold text-lg">Configura il prodotto</h3>
      </div>

      {axes.map((axis) => (
        <div key={axis.key} className="mb-5" data-testid={`axis-${axis.key}`}>
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-sm font-medium">{axis.label}</label>
            <span className="text-xs text-muted-foreground">{axis.options.find((o) => o.value === selection[axis.key])?.label || "—"}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {axis.options.map((opt) => {
              const isSelected = selection[axis.key] === opt.value;
              const isAvailable = isOptionAvailable(axis.key, opt.value);
              return (
                <button
                  key={opt.value}
                  data-testid={`opt-${axis.key}-${opt.value}`}
                  onClick={() => pickOption(axis.key, opt.value)}
                  disabled={!isAvailable && !isSelected}
                  title={opt.label + (opt.price_delta ? ` (+€${opt.price_delta})` : "")}
                  className={`relative flex flex-col items-center transition-all ${!isAvailable ? "opacity-30" : ""}`}
                >
                  {opt.image_url ? (
                    <div
                      className={`w-14 h-14 rounded-sm overflow-hidden border-2 transition-all ${isSelected ? "shadow-md scale-105" : "border-transparent hover:border-neutral-300"}`}
                      style={{ borderColor: isSelected ? primaryColor : undefined }}
                    >
                      <img src={opt.image_url} alt={opt.label} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className={`px-3 py-1.5 rounded-sm border text-xs ${isSelected ? "text-white" : "border-border text-foreground hover:bg-accent"}`}
                      style={{ background: isSelected ? primaryColor : "transparent", borderColor: isSelected ? primaryColor : undefined }}>
                      {opt.label}
                    </div>
                  )}
                  <span className="text-[10px] text-muted-foreground mt-1 max-w-[60px] truncate">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {!matchedVariant && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2" data-testid="combo-unavailable">
          <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">Questa combinazione non è disponibile al momento. Prova a cambiare un&apos;opzione.</div>
        </div>
      )}
    </div>
  );
}
